// AdminLogs.tsx
import React, { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  CalendarIcon,
  EyeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ClipboardDocumentListIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { adminLogsService, type LogsResponse, type AuditLogEntry, type SystemLogEntry } from '@/services/adminLogsService'

const ACTION_LABELS: Record<string, string> = {
  'USER_LOGIN': 'User Login',
  'USER_LOGOUT': 'User Logout',
  'PASSWORD_RESET': 'Password Reset',
  'CREDENTIALS_VIEWED': 'Credentials Viewed',
  'USER_ACTIVATED': 'Account Activated',
  'USER_DEACTIVATED': 'Account Deactivated',
  'ROLE_UPDATED': 'Role Updated',
  'FORCE_PASSWORD_RESET': 'Force Password Reset',
  'BULK_USER_ACTIVATED': 'Bulk Activation',
  'BULK_USER_DEACTIVATED': 'Bulk Deactivation',
  'BULK_FORCE_PASSWORD_RESET': 'Bulk Force Password Reset',
  'VIEW_USER_AUDIT_HISTORY': 'Audit History Viewed',
  'REVOKE_PASSWORD_RESET_TOKENS': 'Password Reset Tokens Revoked',
  'EMPLOYEE_CREATED': 'Employee Created',
  'EMPLOYEE_UPDATED': 'Employee Updated',
  'EMPLOYEE_DELETED': 'Employee Deleted',
  'PAYROLL_RUN': 'Payroll Processed',
  'EXCHANGE_RATE_UPDATED': 'Exchange Rate Updated'
}

const LOG_LEVELS = [
  { value: '', label: 'All Levels' },
  { value: 'INFO', label: 'Info' },
  { value: 'WARNING', label: 'Warning' },
  { value: 'ERROR', label: 'Error' },
  { value: 'DEBUG', label: 'Debug' }
]

const LOG_TYPES = [
  { value: 'audit', label: 'Audit Logs' },
  { value: 'system', label: 'System Logs' }
]

const AdminLogs: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [levelFilter, setLevelFilter] = useState('')
  const [logType, setLogType] = useState('audit')
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  })
  const [showFilters, setShowFilters] = useState(false)
  const [selectedLog, setSelectedLog] = useState<AuditLogEntry | SystemLogEntry | null>(null)

  const pageSize = 10

  const { data: logsData, isLoading, error, refetch } = useQuery<LogsResponse>({
    queryKey: ['admin-logs', currentPage, searchTerm, levelFilter, logType, dateRange],
    queryFn: async () => {
      try {
        const response = await adminLogsService.getLogs({
          page: currentPage,
          limit: pageSize,
          type: logType as 'audit' | 'system',
          search: searchTerm.trim(),
          level: levelFilter,
          startDate: dateRange.start,
          endDate: dateRange.end
        })
        return response
      } catch (error) {
        toast.error('Failed to load logs')
        throw error
      }
    }
  })

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    setCurrentPage(1)
    refetch()
  }

  const handleResetFilters = () => {
    setSearchTerm('')
    setLevelFilter('')
    setDateRange({ start: '', end: '' })
    setCurrentPage(1)
  }

  const getActionColor = (action: string) => {
    switch (action) {
      case 'USER_LOGIN':
      case 'USER_LOGOUT':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30'
      case 'PASSWORD_RESET':
      case 'FORCE_PASSWORD_RESET':
        return 'text-amber-600 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30'
      case 'USER_ACTIVATED':
      case 'EMPLOYEE_CREATED':
        return 'text-emerald-600 bg-emerald-100 dark:text-emerald-400 dark:bg-emerald-900/30'
      case 'USER_DEACTIVATED':
      case 'EMPLOYEE_DELETED':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
      case 'ROLE_UPDATED':
      case 'EMPLOYEE_UPDATED':
        return 'text-purple-600 bg-purple-100 dark:text-purple-400 dark:bg-purple-900/30'
      case 'CREDENTIALS_VIEWED':
        return 'text-indigo-600 bg-indigo-100 dark:text-indigo-400 dark:bg-indigo-900/30'
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
    }
  }

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'ERROR':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/30'
      case 'WARNING':
        return 'text-amber-600 bg-amber-100 dark:text-amber-400 dark:bg-amber-900/30'
      case 'INFO':
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900/30'
      case 'DEBUG':
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
      default:
        return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-700'
    }
  }

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'ERROR':
        return <XCircleIcon className="h-3 w-3" />
      case 'WARNING':
        return <ExclamationTriangleIcon className="h-3 w-3" />
      case 'INFO':
        return <InformationCircleIcon className="h-3 w-3" />
      case 'DEBUG':
        return <DocumentTextIcon className="h-3 w-3" />
      default:
        return <DocumentTextIcon className="h-3 w-3" />
    }
  }

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  }

  const renderActionDetails = (entry: AuditLogEntry) => {
    const details = entry.details || {}

    switch (entry.action) {
      case 'ROLE_UPDATED':
        return (
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            From: <span className="font-medium">{details.oldRole}</span> → To: <span className="font-medium">{details.newRole}</span>
          </div>
        )
      case 'EMPLOYEE_CREATED':
      case 'EMPLOYEE_UPDATED':
        return details.employeeNumber && (
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Employee: {details.employeeNumber}
          </div>
        )
      case 'EXCHANGE_RATE_UPDATED':
        return details.rate && (
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            Rate: 1 USD = {details.rate} CDF
          </div>
        )
      default:
        return null
    }
  }

  const renderLogDetails = (log: SystemLogEntry) => {
    return (
      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
        Source: <span className="font-medium">{log.source}</span>
        {log.userId && ` • User ID: ${log.userId}`}
      </div>
    )
  }

  // Calcul de la pagination correcte avec vérification de l'existence des propriétés
  const currentLogs = logType === 'audit' ? logsData?.auditLogs : logsData?.systemLogs?.logs
  const currentPagination = logType === 'audit'
    ? logsData?.pagination
    : logsData?.systemLogs?.pagination

  const totalPages = currentPagination?.pages || 0
  const totalItems = currentPagination?.total || 0
  const hasPrevious = currentPage > 1
  const hasNext = currentPage < totalPages

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center max-w-md">
          <XCircleIcon className="h-8 w-8 text-red-500 mx-auto mb-2" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Failed to load logs</h2>
          <p className="text-gray-600 dark:text-gray-400 text-xs mb-3">
            There was an error loading the system logs. Please try again.
          </p>
          <button
            onClick={() => refetch()}
            className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm font-medium inline-flex items-center gap-1 px-3 py-2 transition-all duration-200 hover:shadow-md"
          >
            <ArrowPathIcon className="h-3 w-3" />
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-600 flex items-center justify-center text-white">
            <ClipboardDocumentListIcon className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">System Logs</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Monitor system activities, audit trails, and application events
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Total Logs</div>
            <div className="text-gray-900 dark:text-white font-bold text-sm">
              {totalItems.toLocaleString()}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Log Type</div>
            <div className="text-gray-900 dark:text-white font-bold text-sm capitalize">
              {logType}
            </div>
          </div>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <form onSubmit={handleSearch} className="flex-1 max-w-xl">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search logs by user, action, or message..."
                className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs transition-all duration-200"
              />
            </div>
          </form>

          <div className="flex items-center gap-2">
            <select
              value={logType}
              onChange={(e) => {
                setLogType(e.target.value)
                setCurrentPage(1)
              }}
              className="px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs font-medium"
            >
              {LOG_TYPES.map(type => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium inline-flex items-center gap-1 px-3 py-2 hover:bg-slate-50 dark:hover:bg-gray-600 transition-all duration-200"
            >
              <AdjustmentsHorizontalIcon className="h-3 w-3" />
              Filters
            </button>

            {(searchTerm || levelFilter || dateRange.start || dateRange.end) && (
              <button
                onClick={handleResetFilters}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium inline-flex items-center gap-1 px-3 py-2 hover:bg-slate-50 dark:hover:bg-gray-600 transition-all duration-200"
              >
                <ArrowPathIcon className="h-3 w-3" />
                Reset
              </button>
            )}
          </div>
        </div>

        {showFilters && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1">
                  Log Level
                </label>
                <select
                  value={levelFilter}
                  onChange={(e) => {
                    setLevelFilter(e.target.value)
                    setCurrentPage(1)
                  }}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                >
                  {LOG_LEVELS.map(level => (
                    <option key={level.value} value={level.value}>
                      {level.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1">
                  Start Date
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-900 dark:text-white mb-1">
                  End Date
                </label>
                <div className="relative">
                  <CalendarIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                  />
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Logs Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-8">
            <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
            <p className="ml-2 text-xs text-gray-500 dark:text-gray-400">Loading logs...</p>
          </div>
        ) : !currentLogs?.length ? (
          <div className="text-center py-8">
            <DocumentTextIcon className="h-8 w-8 text-gray-400 dark:text-gray-600 mx-auto mb-2" />
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">No logs found</h3>
            <p className="text-gray-600 dark:text-gray-300 text-xs">Try adjusting your search or filters</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-xs">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      {logType === 'audit' ? 'Action' : 'Level'}
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      Details
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      User
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      Timestamp
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      IP Address
                    </th>
                    <th scope="col" className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  {logType === 'audit' ? (
                    (logsData?.auditLogs || []).map((entry: AuditLogEntry) => (
                      <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${getActionColor(entry.action)}`}>
                            {ACTION_LABELS[entry.action] || entry.action}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-900 dark:text-white font-medium">
                            {entry.details?.message || ACTION_LABELS[entry.action] || entry.action}
                            {renderActionDetails(entry)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs font-medium text-gray-900 dark:text-white">
                            {entry.adminUser.firstName} {entry.adminUser.lastName}
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {entry.adminUser.email}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-500 capitalize mt-0.5">
                            {entry.adminUser.role.replace('_', ' ').toLowerCase()}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400 font-medium">
                          {formatDateTime(entry.createdAt)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400 font-mono">
                          {entry.ipAddress}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <button
                            onClick={() => setSelectedLog(entry)}
                            className="text-gray-400 hover:text-indigo-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                            title="View Details"
                          >
                            <EyeIcon className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    (logsData?.systemLogs?.logs || []).map((log: SystemLogEntry) => (
                      <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium ${getLevelColor(log.level)}`}>
                            {getLevelIcon(log.level)}
                            {log.level}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs text-gray-900 dark:text-white font-medium">
                            {log.message}
                            {renderLogDetails(log)}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-xs font-medium text-gray-900 dark:text-white">
                            System
                          </div>
                          <div className="text-xs text-gray-600 dark:text-gray-400">
                            {log.source}
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400 font-medium">
                          {formatDateTime(log.timestamp)}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-600 dark:text-gray-400 font-mono">
                          -
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="text-gray-400 hover:text-indigo-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                            title="View Details"
                          >
                            <EyeIcon className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalItems)} of {totalItems} entries
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={!hasPrevious}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Previous
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        let pageNum
                        if (totalPages <= 5) {
                          pageNum = i + 1
                        } else if (currentPage <= 3) {
                          pageNum = i + 1
                        } else if (currentPage >= totalPages - 2) {
                          pageNum = totalPages - 4 + i
                        } else {
                          pageNum = currentPage - 2 + i
                        }

                        return (
                          <button
                            key={pageNum}
                            onClick={() => setCurrentPage(pageNum)}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              currentPage === pageNum
                                ? 'bg-indigo-600 text-white'
                                : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:bg-slate-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {pageNum}
                          </button>
                        )
                      })}
                    </div>

                    <button
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                      disabled={!hasNext}
                      className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium px-2 py-1 text-xs hover:bg-slate-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Log Details Modal */}
      {selectedLog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
                  <EyeIcon className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                    Log Details
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Complete information about this log entry
                  </p>
                </div>
              </div>
              <button
                onClick={() => setSelectedLog(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <XCircleIcon className="h-4 w-4" />
              </button>
            </div>

            <div className="p-4 overflow-auto max-h-[calc(90vh-120px)]">
              <pre className="text-xs text-gray-900 dark:text-gray-100 whitespace-pre-wrap bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                {JSON.stringify(selectedLog, null, 2)}
              </pre>
            </div>

            <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setSelectedLog(null)}
                className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium px-3 py-2 hover:bg-slate-50 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default AdminLogs